import React, { useEffect, useState } from 'react';
import { decrypted } from 'config/crypto';
import { useDispatch, useSelector } from 'react-redux';
import { getLocalStorageValue } from 'config/helper';
import { clearAllCateringProductToCartListSuccess, clearAllProductToCartListSuccess, getSavePlaceOrder, getSavePlaceOrderCateringAndMeals, savePaymentTransactionId, savePaymentTransactionType } from 'redux/actions';
import { useNavigate } from 'react-router-dom';
import axios from "config/axios";
import { createWallet, getWallets } from 'services/ecrpyt';
import { useApiDefaults } from 'hooks/useApiDefaults';
import { PAYMENT_ERROR_MESSAGE } from 'config/constant';
import ErrorBoundary from 'components/Common/ErrorBoundary';
import { debugLogger } from 'utils/debugLogger';
import { useConfig } from 'hooks/useConfig';

const ECryptPaymentGateway = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [token, setToken] = useState(null);
    const { storeDetails, currentStoreID } = useSelector(({ store }) => store);
    const { onClose, paymentRequestBody, checkoutType } = props;
    const { authorized_amount, member_number, user_token, total_amount } = paymentRequestBody;
    const paymentAmount = checkoutType ? +total_amount : +authorized_amount;
    const storeId = currentStoreID || getLocalStorageValue("selected-store-id");
    const clientId = getLocalStorageValue("RSAclient-id");
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [isDefaultCard, setIsDefaultCard] = useState(false);

    const commonDetailsData = useSelector(({ common }) => common.commonDetailsData);
    const allowTokenization = commonDetailsData?.header_section?.allow_ecrypt_tokenzation === "1"
    const { autoSaveCard } = useConfig();

    const apiDefaults = useApiDefaults()
    const isMealKit = checkoutType === "catering-meal"

    const [orderId, setOrderId] = useState(null);
    const [showSaveCardConfirmationPopup, setShowSaveCardConfirmationPopup] = useState(false);
    const [savingCard, setSavingCard] = useState(false);
    const [saveCardMessage, setSaveCardMessage] = useState({
        message: "",
        error: false
    });

    const [showExistingCardSelectionPopup, setShowExistingCardSelectionPopup] = useState();
    const [gettingSavedCards, setGettingSavedCards] = useState(false);
    const [savedCards, setSavedCards] = useState([]); 
    const [selectedCard, setSelectedCard] = useState("");

    async function getSavedCards() {
        try {
            setGettingSavedCards(true);
            const res = await getWallets(apiDefaults)
            if (res.error) {
                setGettingSavedCards(false);
                return;
            }
    
            const cards = res.data
            setSavedCards(cards);
            
            if (cards?.length) {
                const defaultCard = cards.find(card => card.default)
                if (defaultCard) {
                    setSelectedCard(defaultCard.id)
                }
                setShowExistingCardSelectionPopup(true);
                return false
            } 
            return true
        } catch (error) {
            console.error(error)
            return true
        } finally {
            setGettingSavedCards(false);
        }
    }

    useEffect(() => {
        if (allowTokenization) {
            getSavedCards()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const redirectToOrderConfirmation = () => {
        try {
            debugLogger.log('🔄 ECryptPaymentGateway: Redirecting to order confirmation');
            navigate("/order-confirmation");
            dispatch(clearAllProductToCartListSuccess());
            dispatch(clearAllCateringProductToCartListSuccess());
            debugLogger.log('✅ ECryptPaymentGateway: Navigation and cart clear successful');
        } catch (error) {
            debugLogger.error('❌ ECryptPaymentGateway: Error during redirect', error);
            throw error;
        }
    }

    const handleSaveEcryptTransaction = (response, token, last_new_order_id, askToSaveCard, savedCard) => {
        try {
            debugLogger.log('🔄 ECryptPaymentGateway: handleSaveEcryptTransaction started', {
                response, token, last_new_order_id, askToSaveCard, savedCard
            });

            const { order_id, order_number } = response;
            
            let orderId
            let APIEndPoint
            
            let extraInfo = {}
            if (token) {
                extraInfo.ecrypt_token = token
            } else if (savedCard) {
                extraInfo.ecrypt_customer_wallet_id = savedCard
            }
            
            if (isMealKit) {
                orderId = order_number
                APIEndPoint = "/SaveCateingAndMealkitEcryptTransaction"
                extraInfo.last_catering_or_mealkit_order_id = last_new_order_id
                extraInfo.grand_total = +paymentAmount || 0
            } else {
                orderId = order_id
                APIEndPoint = "/AogSaveEcryptTransaction"
                extraInfo.last_new_order_id = last_new_order_id
                extraInfo.authorized_amount = +paymentAmount || 0
                extraInfo.grand_plus_discount_total = +paymentAmount || 0
                extraInfo.payment_type = "ecrypt"
            }

            debugLogger.log('🔄 ECryptPaymentGateway: API endpoint and order details', {
                orderId, APIEndPoint, extraInfo
            });

            setOrderId(last_new_order_id);
            setPaymentLoading(true);
            dispatch(savePaymentTransactionId(token));
            dispatch(savePaymentTransactionType("ecrypt"));
            setPaymentError("");

            const body = {
                ...extraInfo,
                user_token,
                AppName: "shop",
                DeviceType: "web",
                ClientStoreId: +storeId || 1,
                Version: 1,
                RSAClientId: clientId,
                order_number: orderId,
                member_number,
            };

            debugLogger.log('🔄 ECryptPaymentGateway: Sending request to', APIEndPoint);

            axios
                .post(APIEndPoint, body)
                .then(async res => {
                    debugLogger.log('✅ ECryptPaymentGateway: API response received', res.data);
                    setPaymentLoading(false);
                    
                    if (res.data.status === "success") {
                        debugLogger.log('✅ ECryptPaymentGateway: Payment successful');
                        
                        if (autoSaveCard) {
                            if (!savedCard) {
                                debugLogger.log('🔄 ECryptPaymentGateway: Auto-saving card');
                                await saveCard(last_new_order_id, autoSaveCard)
                            }
                            redirectToOrderConfirmation()
                        } else if (allowTokenization && askToSaveCard) {
                            debugLogger.log('🔄 ECryptPaymentGateway: Showing save card confirmation');
                            setShowSaveCardConfirmationPopup(true);
                        } else {
                            redirectToOrderConfirmation()
                        }
                    }
                    else {
                        debugLogger.error('❌ ECryptPaymentGateway: Payment failed', res.data?.message);
                        setPaymentError(res.data?.message || PAYMENT_ERROR_MESSAGE);
                    }
                })
                .catch((error) => {
                    debugLogger.error('❌ ECryptPaymentGateway: API request failed', error);
                    onClose();
                    setPaymentLoading(false);
                    setPaymentError(error.message || PAYMENT_ERROR_MESSAGE);
                });
        } catch (error) {
            debugLogger.error('❌ ECryptPaymentGateway: handleSaveEcryptTransaction error', error);
            setPaymentLoading(false);
            setPaymentError(error.message || PAYMENT_ERROR_MESSAGE);
            throw error;
        }
    };

    const saveEcryptTransaction = ({askToSaveCard, token, savedCard}) => {
      try {
          debugLogger.log('🔄 ECryptPaymentGateway: saveEcryptTransaction started', {
              askToSaveCard, token, savedCard
          });

          setPaymentLoading(true);

          if (paymentLoading) {
              debugLogger.log('⚠️ ECryptPaymentGateway: Payment already in progress');
              return;
          }

          let getSavePlaceOrderFunc = getSavePlaceOrder
          let lastNewOrderIdKey = "last_new_order_id"
          if (isMealKit) {
              getSavePlaceOrderFunc = getSavePlaceOrderCateringAndMeals
              lastNewOrderIdKey = "last_catering_or_mealkit_order_id"
          }

          debugLogger.log('🔄 ECryptPaymentGateway: Dispatching place order');

          dispatch(getSavePlaceOrderFunc(paymentRequestBody, false))
              .then((res) => {
                  debugLogger.log('✅ ECryptPaymentGateway: Place order response', res.data);
                  
                  if (res.data.status === "error") {
                      debugLogger.error('❌ ECryptPaymentGateway: Place order failed', res.data);
                      onClose()
                      return;
                  }
                  
                  handleSaveEcryptTransaction(
                      res.data.data,
                      token,
                      res.data[lastNewOrderIdKey],
                      askToSaveCard,
                      savedCard
                  );
              })
              .catch((error) => {
                  debugLogger.error('❌ ECryptPaymentGateway: Place order promise rejected', error);
                  setPaymentLoading(false);
                  setPaymentError(error.message || PAYMENT_ERROR_MESSAGE);
              });
      } catch (error) {
          debugLogger.error('❌ ECryptPaymentGateway: saveEcryptTransaction error', error);
          setPaymentLoading(false);
          setPaymentError(error.message || PAYMENT_ERROR_MESSAGE);
          throw error;
      }
    };

    async function saveCard(orderId, autoSaveCard) {
      const orderIdKey = isMealKit ? "meal_order_id" : "order_id"
      const res = await createWallet({...apiDefaults, [orderIdKey]: orderId, is_default: isDefaultCard || autoSaveCard ? 1 : 0})
      return res
    }

    async function handleSaveCard(isSaveCard) {
        try {
            setSaveCardMessage({
                message: "",
                error: false
            });
            setSavingCard(true);
    
            if (savingCard) {
                return;
            }
    
            if (isSaveCard) {
                const res = await saveCard(orderId)
                if (res.error) {
                    setSaveCardMessage({
                        message: res.message,
                        error: true
                    });
                    setSavingCard(false);
                    return;
                } 

                setSaveCardMessage({
                    message: res.message,
                    error: false
                });
            }
            setSavingCard(false);

            setTimeout(() => {
                redirectToOrderConfirmation()
            }, 2000);
        } catch (error) {
            setSaveCardMessage({
                message: error.message || "Something went wrong",
                error: true
            });
            setSavingCard(false);
        }
    }

    function handlePayWithExisitngCard() {
        saveEcryptTransaction({askToSaveCard: false, savedCard: selectedCard})
    }

    function handlePayWithNewCard() {
        setShowExistingCardSelectionPopup(false);
    }
    
    if (gettingSavedCards) {
        return (
          <PaymentModal>
            <div style={{textAlign: 'center'}}>Loading...</div>
          </PaymentModal>
        )
      }

    if (showExistingCardSelectionPopup) {
        return (
          <PaymentModal>
            <div
              className="close-btn"
              onClick={() => setShowExistingCardSelectionPopup(false)}
            >
              <i className="fa-solid fa-xmark"></i>
            </div>

            <div className='payment-card-detail'>
              <div className='new-card'>
               {paymentLoading ? null : <button
               className='new-card-wrap'
                  disabled={paymentLoading}
                  onClick={() => handlePayWithNewCard()}
                >
                  {" "}
                 Pay with New Card{" "}
                </button>}
              </div>
              <div className='title'>Pay with saved card</div>
              <div>
                <select
                  value={selectedCard}
                  onChange={e => setSelectedCard(e.target.value)}
                  id="card-select"
                  disabled={paymentLoading}
                >
                  <option value="">Select a card</option>
                  {savedCards.map(wallet => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.account_number} {wallet.network}
                    </option>
                  ))}
                </select>
              </div>

              <div className='payment-btn'>
                <button
                  disabled={paymentLoading || !selectedCard}
                  onClick={() => handlePayWithExisitngCard()}
                >
                  {" "}
                 {paymentLoading ? "Processing" : `Pay with selected card ($${paymentAmount.toFixed(2)})`}
                </button>
                {/* {paymentLoading ? null : <button
                  disabled={paymentLoading}
                  onClick={() => handlePayWithNewCard()}
                >
                  {" "}
                  Pay with new card{" "}
                </button>} */}
              </div>
              {paymentError && <div className="error-msg" style={{ color: 'red', marginTop: '12px', marginBottom: '12px' }}>{paymentError}</div>}
            </div>
          </PaymentModal>
        )
    }

    if (showSaveCardConfirmationPopup) {
        const showingSuccessMessage = saveCardMessage.message && !saveCardMessage?.error
        const disabled = savingCard || showingSuccessMessage

        return (
          <PaymentModal>
              <div  className='help-text'>
                Would you like to save this card securely for faster checkout?
              </div>
             
              <div className='save-card-wrap'>
                  <input type="checkbox" id="save_card" checked={isDefaultCard} onChange={e => setIsDefaultCard(e.target.checked)} />
                  <label htmlFor="save_card" style={{ display: 'flex', alignItems: 'center' , justifyContent: 'center'}}>
                    <p>Make this default payment method</p>
                  </label>
                </div>
              <div className='conformation-btn'>
                <button disabled={disabled} onClick={() => handleSaveCard(false)}>
                  No
                </button>
                <button className='button-primary' disabled={disabled} onClick={() => handleSaveCard(true)}>
                  {savingCard ? "Saving..." : "Yes"}
                </button>
              </div>
              {saveCardMessage.message 
              ? <div className={`${saveCardMessage.error ? "error-msg" : "success-msg"}`}>
                    {saveCardMessage.message}
                </div> 
              : null}
          </PaymentModal>
        )
      }

    return (
        <ErrorBoundary 
            boundaryName="ECryptPaymentGateway-Main"
            onError={(error, errorInfo, boundaryName) => {
                console.error(`Error in ${boundaryName}:`, error);
                setPaymentError(error.message || PAYMENT_ERROR_MESSAGE);
            }}
        >
            <PaymentModal>
                    <span className="help-text">Please enter credit card details below</span>
                    
                    <ErrorBoundary boundaryName="EcryptIframe-Wrapper">
                        <EcryptIframe setToken={setToken} storeDetails={storeDetails} />
                    </ErrorBoundary>

                    <div
                        className="close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </div>
                   
                    {paymentError && <div className="error-msg" style={{ color: 'red', marginBottom: '12px' }}>{paymentError}</div>}
                    
                    <div className="pay-btn-wrap">
                        <button
                            disabled={!token}
                            onClick={() => saveEcryptTransaction({askToSaveCard: true, token})}
                            className={`${(!token || paymentLoading) ? 'pay-btn-disabled' : 'pay-btn '}`}
                        >
                            {paymentLoading ? "Processing" : `Pay $${paymentAmount.toFixed(2)}`}
                        </button>
                    </div>
            </PaymentModal>
        </ErrorBoundary>
    );
};

function PaymentModal({children}) {
  return (
    <div className="modal-backdrop payment-model-wrap">
      <div className="modal-lg place-order-popup store-payment-modal">
        {children}
      </div>
    </div>
  )
}

export function EcryptIframe({setToken, storeDetails}) {
    useEffect(() => {
        try {
            debugLogger.log('🔄 EcryptIframe: Initializing iframe');
            
            const ecryptConfigScript = document.createElement('script');
            const publicKey = decrypted(storeDetails?.repuk);
            
            debugLogger.log('🔄 EcryptIframe: Public key decrypted', publicKey ? 'Success' : 'Failed');
            
            ecryptConfigScript.type = 'text/javascript';
            ecryptConfigScript.innerHTML = `
                var ecryptConfig = {
                appearance: {
                bodyBackground: "#fff",
                formBackground: "#fff",
                inputColor: "#000",
                inputBorderColor: "#1A1A1A",
                inputBackground: "#fff",
                errorColor: "#d90429",
                successColor: "#2BBD53",
                focusColor: "#000",
                hidealerts: false,
                googleFontFamily: "'Roboto', sans-serif",
                },
                theme: "long-form-border-light",
                publicKey: "${publicKey}",
                
                };
            `;
            document.body.appendChild(ecryptConfigScript);
            debugLogger.log('✅ EcryptIframe: Config script added');

            const ecryptJSScript = document.createElement('script');
            ecryptJSScript.src = 'https://iframe.ecrypt.com/js/ecrypt_iframe.js';
            ecryptJSScript.onerror = (error) => {
                debugLogger.error('❌ EcryptIframe: Failed to load ecrypt script', error);
            };
            ecryptJSScript.onload = () => {
                debugLogger.log('✅ EcryptIframe: Ecrypt script loaded successfully');
            };
            document.body.appendChild(ecryptJSScript);

            const messageHandler = event => {
              try {
                  if (event.origin !== window.location.origin) {
                      var msg = event.data;
                      if (typeof msg === 'string') {
                        if (msg.includes('token')) {
                          debugLogger.log('✅ EcryptIframe: Token received from iframe');
                          const parsedMsg = JSON.parse(msg);
                          const parseResponse = JSON.parse(parsedMsg);
                          setToken(parseResponse.token);
                        } else {
                          debugLogger.log('⚠️ EcryptIframe: Message received but no token');
                          setToken(null);
                        }
                      }
                  }
              } catch (error) {
                  debugLogger.error('❌ EcryptIframe: Message handler error', error);
              }
            };

            window.addEventListener('message', messageHandler);
            debugLogger.log('✅ EcryptIframe: Message handler attached');

            return () => {
              try {
                  debugLogger.log("🔄 EcryptIframe: Starting cleanup")
                  window.removeEventListener('message', messageHandler);
                  debugLogger.log("✅ 1 EcryptIframe: messageHandler removed")
                  
                  if (ecryptConfigScript && ecryptConfigScript.parentNode) {
                    ecryptConfigScript.parentNode.removeChild(ecryptConfigScript);
                    debugLogger.log("✅ 2 EcryptIframe: ecryptConfigScript removed")
                  }

                  if (ecryptJSScript && ecryptJSScript.parentNode) {
                    ecryptJSScript.parentNode.removeChild(ecryptJSScript);
                    debugLogger.log("✅ 3 EcryptIframe: ecryptJSScript removed")
                  }
                  
                  const iframeWrapper = document.getElementById('ecrypt-iframe-wrapper');
                  if (iframeWrapper) {
                    iframeWrapper.innerHTML = '';
                    debugLogger.log("✅ 4 EcryptIframe: iframeWrapper cleared")
                  }
              } catch (error) {
                  debugLogger.error('❌ EcryptIframe: Cleanup error', error);
              }
            }
        } catch (error) {
            debugLogger.error('❌ EcryptIframe: Initialization error', error);
            throw error;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <div id="ecrypt-iframe-wrapper"></div>
}
  
export default ECryptPaymentGateway;

// gettingCards showLoader
// if cards
//   show card selection
//     1. pay with existing card
//         pay and redirection to order confirmation
//     2. pay with new card
//         show iframe and then pay 
//             ask to save card 
//                 if yes then save card and redirection to order confirmation
//                 if no then redirection to order confirmation
// else 
//   show iframe and then pay
//     ask to save card
//         if yes then save card and redirection to order confirmation
//         if no then redirection to order confirmation